import { inject, Injectable } from '@angular/core'
import { DepartmentService } from '@ftr/api-core'
import { RecordingTags } from '@ftr/contracts/api/recordings-index'
import { VocabularyTerms } from '@ftr/contracts/type/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { LocationService } from '@ftr/data-location'
import { titleCase, unwrapData } from '@ftr/foundation'
import {
  FilterDateCountInterval,
  FilterDescriptor,
  FilterKey,
  FilterLabelData,
  FilterStateModel,
  FilterType,
  FilterValueData,
  RelativeDateRangeKey,
} from '@ftr/lists'
import { LaunchDarklyService } from '@ftr/ui-feature-flags'
import { VocabularyTermsService } from '@ftr/ui-vocab'
import { convert, Duration, LocalDate, LocalTime } from '@js-joda/core' // Added LocalTime import
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { firstValueFrom } from 'rxjs'
import {
  ClearRecordingsFilterStateAction,
  ClearRecordingsFilterTypeStateAction,
  SetRecordingsFilterStateAction,
  SyncRecordingsFilterState,
} from './recordings-filter.actions'

export const FilterTypeMapping: Partial<Record<FilterType, FilterKey[]>> = {
  [FilterType.Location]: [FilterKey.Courthouse, FilterKey.Courtroom],
  [FilterType.Department]: [FilterKey.Department],
  [FilterType.Date]: [FilterKey.DateFrom, FilterKey.DateTo, FilterKey.DateCountInterval],
  [FilterType.AdvancedFilters]: [
    FilterKey.DurationFrom,
    FilterKey.DurationTo,
    FilterKey.TimeFrom,
    FilterKey.TimeTo,
    FilterKey.Tags,
    FilterKey.ReporterLocation,
  ],
}

export function defaultRecordingsFilterState(): FilterStateModel {
  // TODO: Set default filter to min duration of 300 seconds
  // Okay to leave for now for testing purposes, but need to update this before release
  return {
    values: {},
    labels: {},
  }
}

@State<FilterStateModel>({
  name: 'recordingsFilterState',
  defaults: defaultRecordingsFilterState(),
})
@Injectable()
export class RecordingsFilterState {
  constructor(
    private readonly locationService: LocationService,
    private readonly departmentService: DepartmentService,
    private readonly vocabularyTermsService: VocabularyTermsService,
  ) {}

  private readonly launchDarklyService = inject(LaunchDarklyService)

  @Selector()
  static getFilterState(state: FilterStateModel): FilterStateModel {
    return state
  }

  @Action(SyncRecordingsFilterState)
  async syncRecordingsFilterState(
    { dispatch }: StateContext<FilterStateModel>,
    { courtSystemId, filterValues }: SyncRecordingsFilterState,
  ): Promise<void> {
    await firstValueFrom(
      dispatch(new SetRecordingsFilterStateAction(courtSystemId, filterValues, {}, Object.values(FilterType), false)),
    )
  }

  @Action(ClearRecordingsFilterStateAction)
  clearFilterState({ patchState }: StateContext<FilterStateModel>): void {
    patchState(defaultRecordingsFilterState())
  }

  @Action(ClearRecordingsFilterTypeStateAction)
  clearFilterTypeState(
    ctx: StateContext<FilterStateModel>,
    { filterType }: ClearRecordingsFilterTypeStateAction,
  ): void {
    const state = ctx.getState()
    const newFilteredValues = { ...state.values }
    const newFilteredLabels = { ...state.labels }
    const filterKeys = FilterTypeMapping[filterType as FilterType]

    filterKeys?.forEach(key => {
      newFilteredValues[key] = undefined
    })

    newFilteredLabels[filterType as FilterType] = undefined

    ctx.patchState({
      values: newFilteredValues,
      labels: newFilteredLabels,
    })
  }

  @Action(SetRecordingsFilterStateAction)
  async setFilterValueState(
    { getState, patchState }: StateContext<FilterStateModel>,
    { courtSystemId, filterValues, filterLabels, generateLabelTypes, patch }: SetRecordingsFilterStateAction,
  ): Promise<void> {
    const currentState = getState()
    // Ensure filterValues keys are of type FilterKey
    const typedFilterValues = filterValues as Partial<
      Record<FilterKey, string | number | string[] | LocalDate | LocalTime>
    >

    const newFilteredValues = patch
      ? removeUndefinedValues({
          ...currentState.values,
          ...typedFilterValues, // Use typed values
        })
      : typedFilterValues // Use typed values

    const newFilteredLabels: FilterLabelData = {
      ...currentState.labels,
      ...filterLabels,
    }

    const filterTypeLabelCount = {
      [FilterType.Location]: 0,
      [FilterType.Department]: 0,
      [FilterType.Date]: 0,
      [FilterType.AdvancedFilters]: 0,
    }

    const replaceRealTimeWordingFlagEnabled = await this.launchDarklyService.getFlagVariationForAnonymous<boolean>(
      'replace-realtime-wording',
      courtSystemId,
    )
    // Filter label generation
    const skipKeys: FilterKey[] = [] // Use FilterKey
    for (const filterType of generateLabelTypes) {
      // Clear current label for filter type
      newFilteredLabels[filterType as FilterType] = undefined

      // Loop over all filter keys of the filter type
      for (const filterKey of FilterTypeMapping[filterType as FilterType] ?? []) {
        // Skip if filter key is already processed or if filter value is undefined
        // Direct access with FilterKey
        if (skipKeys.includes(filterKey) || newFilteredValues[filterKey] === undefined) {
          continue
        }

        const [label, processedFilterValueKeys] = await this.generateFilterLabel(
          courtSystemId,
          newFilteredValues,
          filterKey, // Pass FilterKey
          replaceRealTimeWordingFlagEnabled,
        )

        // Check if filterType is a valid key before incrementing
        if (filterType in filterTypeLabelCount) {
          // Increment the count for the current filter type
          filterTypeLabelCount[filterType as keyof typeof filterTypeLabelCount] += 1

          // Check the updated count to set the label
          if (filterTypeLabelCount[filterType as keyof typeof filterTypeLabelCount] <= 1) {
            newFilteredLabels[filterType as FilterType] = label
          } else {
            const fallbackLabel = `${filterTypeLabelCount[filterType as keyof typeof filterTypeLabelCount]} Selected`
            newFilteredLabels[filterType as FilterType] = {
              label: fallbackLabel,
            }
          }
        }

        // Some labels are generated from multiple filter keys, so we need to skip those keys
        skipKeys.push(...processedFilterValueKeys)
      }
    }

    patchState({
      values: newFilteredValues,
      labels: newFilteredLabels,
    })
  }

  /**
   * Generates a filter label and associated filter keys based on the provided filter values and key.
   *
   * Some Filters have multiple keys that are used to generate a single label, so we need to return the keys that were used.
   * Example:
   *   DurationFrom and DurationTo can be used to generate a single label, so both FilterKey.DurationFrom and FilterKey.DurationTo are returned.
   *   Generating for the filter key FilterKey.DurationFrom would return the label "Greater than 1 hour" or "Duration between 1 hour - 2 hours" depending
   *   if DurationTo was also set, and will also return the keys FilterKey.DurationFrom and FilterKey.DurationTo either way.
   *
   * @param courtSystemId The current court system.
   * @param filterValues The filter values object.
   * @param filterKey The target filter key to be used to generate the label.
   * @returns A Promise containing the filter label and filter keys used. If a filterKey is not handled "Filter Selected" is returned.
   */

  private async generateFilterLabel(
    courtSystemId: Uuid,
    filterValues: FilterValueData,
    filterKey: FilterKey, // Accept FilterKey
    replaceRealTimeWordingFlagEnabled: boolean,
  ): Promise<[FilterDescriptor, FilterKey[]]> {
    // Return FilterKey[]
    const fallbackFilterLabel = { label: 'Filter Selected' }
    let filterKeys: FilterKey[] = [] // Use FilterKey
    let filterLabel: FilterDescriptor | undefined

    switch (filterKey) {
      case FilterKey.Courthouse:
        ;[filterLabel, filterKeys] = await this.generateLabelCourthouse(filterValues)
        break
      case FilterKey.Courtroom:
        ;[filterLabel, filterKeys] = await this.generateLabelCourtroom(filterValues)
        break
      case FilterKey.Department:
        ;[filterLabel, filterKeys] = await this.generateLabelDepartment(filterValues)
        break
      case FilterKey.Tags:
        ;[filterLabel, filterKeys] = await this.generateLabelTags(
          courtSystemId,
          filterValues,
          replaceRealTimeWordingFlagEnabled,
        )
        break
      case FilterKey.ReporterLocation:
        ;[filterLabel, filterKeys] = this.generateLabelReporterLocation(filterValues)
        break
      case FilterKey.DurationFrom:
      case FilterKey.DurationTo:
        ;[filterLabel, filterKeys] = this.generateLabelDuration(filterValues)
        break
      case FilterKey.TimeFrom:
      case FilterKey.TimeTo:
        ;[filterLabel, filterKeys] = this.generateLabelContentBetween(filterValues)
        break
      case FilterKey.DateFrom:
      case FilterKey.DateTo:
      case FilterKey.DateCountInterval:
        ;[filterLabel, filterKeys] = this.generateLabelContentWithinDateRange(filterValues)
        break
      default:
        break
    }

    return [filterLabel ?? fallbackFilterLabel, filterKeys]
  }

  private async generateLabelCourthouse(filterValues: FilterValueData): Promise<[FilterDescriptor, FilterKey[]]> {
    // Return FilterKey[]
    const filterKeys: FilterKey[] = [FilterKey.Courthouse] // Use FilterKey
    try {
      const courthouseLocation = await firstValueFrom(
        this.locationService.get(filterValues[FilterKey.Courthouse] as string).pipe(unwrapData()),
      )
      const filterLabel = { label: courthouseLocation!.name }
      return [filterLabel, filterKeys]
    } catch {
      const filterLabel = { label: 'Courthouse Selected' } // Fallback
      return [filterLabel, filterKeys]
    }
  }

  private async generateLabelCourtroom(filterValues: FilterValueData): Promise<[FilterDescriptor, FilterKey[]]> {
    // Return FilterKey[]
    const filterKeys: FilterKey[] = [FilterKey.Courtroom] // Use FilterKey
    try {
      const courtroomLocation = await firstValueFrom(
        this.locationService.getCourtroom(filterValues[FilterKey.Courtroom] as string).pipe(unwrapData()),
      )
      const filterLabel = { label: courtroomLocation!.parent!.name, subLabel: courtroomLocation!.name }
      return [filterLabel, filterKeys]
    } catch {
      const filterLabel = { label: 'Courtroom Selected' } // Fallback
      return [filterLabel, filterKeys]
    }
  }

  private async generateLabelDepartment(filterValues: FilterValueData): Promise<[FilterDescriptor, FilterKey[]]> {
    // Return FilterKey[]
    const filterKeys: FilterKey[] = [FilterKey.Department] // Use FilterKey
    try {
      const department = await firstValueFrom(
        this.departmentService.get(filterValues[FilterKey.Department] as string).pipe(unwrapData()),
      )
      const filterLabel = { label: department!.name }
      return [filterLabel, filterKeys]
    } catch {
      const filterLabel = { label: 'Department Selected' } // Fallback
      return [filterLabel, filterKeys]
    }
  }

  private async generateLabelTags(
    courtSystemId: Uuid,
    filterValues: FilterValueData,
    replaceRealTimeWordingFlagEnabled: boolean,
  ): Promise<[FilterDescriptor, FilterKey[]]> {
    // Return FilterKey[]
    const tags = filterValues[FilterKey.Tags] as string[]
    let label: string

    if (tags.length > 1) {
      label = `${tags.length} Tags Selected`
    } else {
      try {
        const terms = await firstValueFrom(this.vocabularyTermsService.observeTermsSnapshot(courtSystemId))
        label = getTagLabel(tags[0], terms, replaceRealTimeWordingFlagEnabled)
      } catch {
        label = tags[0]
      }
    }

    return [{ label }, [FilterKey.Tags]]
  }

  private generateLabelReporterLocation(filterValues: FilterValueData): [FilterDescriptor, FilterKey[]] {
    // Return FilterKey[]
    const filterLabel = { label: `${filterValues[FilterKey.ReporterLocation]}` }
    const filterKeys: FilterKey[] = [FilterKey.ReporterLocation]
    return [filterLabel, filterKeys]
  }

  private generateLabelDuration(filterValues: FilterValueData): [FilterDescriptor | undefined, FilterKey[]] {
    // Return FilterKey[]
    const filterKeys: FilterKey[] = [FilterKey.DurationFrom, FilterKey.DurationTo]
    const filterLabel = formatTimeBetween(
      'Greater Than',
      'Less Than',
      'Duration Between',
      formatDuration(filterValues[FilterKey.DurationFrom] as number | undefined),
      formatDuration(filterValues[FilterKey.DurationTo] as number | undefined),
    )
    return [filterLabel ? { label: filterLabel } : undefined, filterKeys]
  }

  private generateLabelContentBetween(filterValues: FilterValueData): [FilterDescriptor, FilterKey[]] {
    // Return FilterKey[]
    const filterKeys: FilterKey[] = [FilterKey.TimeFrom, FilterKey.TimeTo]
    let filterLabel = 'Content'
    if (filterValues[FilterKey.TimeFrom] && filterValues[FilterKey.TimeTo]) {
      filterLabel = `${filterLabel} Between ${filterValues[FilterKey.TimeFrom]} - ${filterValues[FilterKey.TimeTo]}`
    } else if (filterValues[FilterKey.TimeFrom]) {
      filterLabel = `${filterLabel} From ${filterValues[FilterKey.TimeFrom]}`
    } else if (filterValues[FilterKey.TimeTo]) {
      filterLabel = `${filterLabel} To ${filterValues[FilterKey.TimeTo]}`
    }

    return [{ label: filterLabel }, filterKeys]
  }

  private generateLabelContentWithinDateRange(filterValues: FilterValueData): [FilterDescriptor, FilterKey[]] {
    // Return FilterKey[]
    const filterKeys: FilterKey[] = [FilterKey.DateFrom, FilterKey.DateTo, FilterKey.DateCountInterval]
    let filterLabel: string
    const rangeType = filterValues[FilterKey.DateCountInterval] as string
    if (rangeType === FilterDateCountInterval.Month) {
      filterLabel = this.generateMonthRangeLabel(filterValues)
    } else if (rangeType === FilterDateCountInterval.Day) {
      filterLabel = this.generateSingleDayLabel(filterValues)
    } else if (rangeType === FilterDateCountInterval.DayRange) {
      filterLabel = this.generateDateRangeLabel(filterValues)
    } else if (rangeType?.includes(FilterDateCountInterval.Relative)) {
      // Add null check for rangeType
      // Relative interval includes a Duration parseable value
      filterLabel = this.generateRelativeRangeLabel(filterValues)
    } else {
      filterLabel = this.generateDateRangeLabel(filterValues)
    }

    return [{ label: filterLabel }, filterKeys]
  }

  private generateMonthRangeLabel(filterValues: FilterValueData): string {
    const startDateString = filterValues[FilterKey.DateFrom] as LocalDate
    const startDate = convert(startDateString).toDate()
    return startDate.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    })
  }

  private generateRelativeRangeLabel(filterValues: FilterValueData): string {
    const rangeKey = filterValues[FilterKey.DateCountInterval] as RelativeDateRangeKey
    switch (rangeKey) {
      case RelativeDateRangeKey.Today:
        return 'Today'
      case RelativeDateRangeKey.Yesterday:
        return 'Yesterday'
      case RelativeDateRangeKey.ThisWeek:
        return 'This Week'
      case RelativeDateRangeKey.LastWeek:
        return 'Last Week'
      default:
        return 'Last X Days'
    }
  }

  private generateSingleDayLabel(filterValues: FilterValueData): string {
    const selectedDate = convert(filterValues[FilterKey.DateFrom] as LocalDate).toDate()
    return selectedDate.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  private generateDateRangeLabel(filterValues: FilterValueData): string {
    const startDate = convert(filterValues[FilterKey.DateFrom] as LocalDate).toDate()
    const endDate = convert(filterValues[FilterKey.DateTo] as LocalDate).toDate()
    const formatOptions: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }
    const startString = startDate.toLocaleDateString('en-US', formatOptions)
    const endString = endDate.toLocaleDateString('en-US', formatOptions)
    return `${startString} - ${endString}`
  }
}

function removeUndefinedValues(obj: FilterValueData): FilterValueData {
  const filteredObj: FilterValueData = {}

  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== '') {
      // Ensure key is treated as FilterKey
      filteredObj[key as FilterKey] = value
    }
  }

  return filteredObj
}

export function getTagLabel(tag: string, terms: VocabularyTerms, replaceRealTimeWordingFlagEnabled: boolean): string {
  if (tag === RecordingTags.Realtime) {
    if (replaceRealTimeWordingFlagEnabled) {
      return 'Justice Cloud'
    }
    return 'RealTime'
  }

  if (tag === RecordingTags.FTRGold) {
    return 'Gold'
  }

  if (tag === RecordingTags.Sealed) {
    return titleCase(terms.sealed.singular)
  }

  return tag
}

/**
 * Formats a duration range or single duration given specific prefixes for start, end, and between.
 *
 * @param startPrefix The prefix to be used for the start of the duration range.
 * @param endPrefix The prefix to be used for the end of the duration range.
 * @param betweenPrefix The prefix to be used for the duration range when both start and end are provided.
 * @param start The start of the duration range.
 * @param end The end of the duration range.
 * @returns The formatted duration range as a string, or undefined if both durations were not provided.
 */
function formatTimeBetween(
  startPrefix: string,
  endPrefix: string,
  betweenPrefix: string,
  start?: string,
  end?: string,
): string | undefined {
  if (start && end) {
    return `${betweenPrefix} ${start} - ${end}`
  } else if (start) {
    return `${startPrefix} ${start}`
  } else if (end) {
    return `${endPrefix} ${end}`
  }
  return undefined
}

/**
 * Formats a duration given in seconds into a human-readable string representation.
 *
 * @param durationInSeconds The duration in seconds to be formatted.
 * @returns The formatted duration as a string, or undefined if the input is not provided.
 */
function formatDuration(durationInSeconds?: number): string | undefined {
  if (!durationInSeconds) {
    return undefined
  }

  const duration = Duration.ofSeconds(durationInSeconds)
  const hours = duration.toHours()
  const minutes = duration.toMinutes() % 60

  let result = ''
  if (hours > 0) {
    result += `${hours} hr${hours > 1 ? 's' : ''}`
  }
  if (minutes > 0) {
    result += ` ${minutes} min${minutes > 1 ? 's' : ''}`
  }

  return result.trim()
}
